import { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import EmptyState from '@/components/admin/EmptyState';
import { adminApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Trash2, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '@/components/admin/ImageUpload';

const EMPTY = {
    platform: 'instagram',
    handle: '@kchediversiones',
    embed_url: '',
    post_url: '',
    embed_code: '',
    image_url: '',
    title: '',
    description: '',
    status: 'published',
    featured: false,
    order: 0,
};

function Form({ value, onChange }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    return (
        <div className="grid gap-3 sm:grid-cols-2">
            <div>
                <Label>Plataforma</Label>
                <Select value={value.platform} onValueChange={(v) => set('platform', v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Estado</Label>
                <Select value={value.status} onValueChange={(v) => set('status', v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="draft">Borrador</SelectItem>
                        <SelectItem value="published">Publicado</SelectItem>
                        <SelectItem value="hidden">Oculto</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="sm:col-span-2"><Label>Título</Label><Input value={value.title} onChange={(e) => set('title', e.target.value)} className="mt-1" placeholder="Ej: Cumpleaños inolvidable" /></div>
            <div className="sm:col-span-2"><Label>Descripción manual</Label><Textarea rows={3} value={value.description} onChange={(e) => set('description', e.target.value)} className="mt-1" placeholder="Esta descripción se mostrará siempre en la tarjeta, aunque el embed falle." /></div>
            <div className="sm:col-span-2"><Label>URL del post original *</Label><Input value={value.post_url} onChange={(e) => set('post_url', e.target.value)} className="mt-1" placeholder="https://www.instagram.com/p/..." />
                <p className="text-xs text-muted-foreground mt-1">El botón “Ver publicación original” abrirá esta URL en una nueva pestaña.</p>
            </div>
            <div className="sm:col-span-2">
                <Label>Código embed</Label>
                <Textarea rows={5} value={value.embed_code} onChange={(e) => set('embed_code', e.target.value)} className="mt-1 font-mono text-xs" placeholder='Pega aquí el código HTML del embed (blockquote de Instagram/TikTok o iframe de Facebook).' />
                <p className="text-xs text-muted-foreground mt-1">Si el embed falla o queda vacío, mostraremos la imagen miniatura como respaldo.</p>
            </div>
            <div className="sm:col-span-2"><ImageUpload value={value.image_url} onChange={(v) => set('image_url', v)} label="Imagen miniatura (fallback)" testId="social-image" /></div>
            <div><Label>Handle</Label><Input value={value.handle} onChange={(e) => set('handle', e.target.value)} className="mt-1" /></div>
            <div><Label>Orden</Label><Input type="number" value={value.order} onChange={(e) => set('order', Number(e.target.value))} className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={value.featured} onCheckedChange={(v) => set('featured', v)} /> Destacado</div>
        </div>
    );
}

export default function AdminSocialPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const load = () => { setLoading(true); adminApi.social.list().then(setItems).finally(() => setLoading(false)); };
    useEffect(() => { load(); }, []);
    const save = async () => {
        if (creating && !form.post_url) {
            toast.error('La URL del post original es obligatoria.');
            return;
        }
        try {
            if (creating) { await adminApi.social.create(form); toast.success('Creado'); setCreating(false); }
            else if (editing) { await adminApi.social.update(editing, form); toast.success('Actualizado'); setEditing(null); }
            load();
        } catch { toast.error('No se pudo guardar'); }
    };
    const remove = async (id) => { await adminApi.social.delete(id); toast.success('Eliminado'); load(); };
    return (
        <AdminLayout title="Social">
            <div className="flex items-center justify-between mb-4"><p className="text-sm text-muted-foreground">Publica embeds y tarjetas manuales para Instagram, TikTok y Facebook.</p>
                <Dialog open={creating} onOpenChange={setCreating}>
                    <DialogTrigger asChild><button onClick={() => { setForm(EMPTY); setCreating(true); }} data-testid="admin-social-new" className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm"><Plus className="size-4" /> Nuevo post</button></DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Nuevo post</DialogTitle></DialogHeader><Form value={form} onChange={setForm} /><DialogFooter><button onClick={save} className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm">Guardar</button></DialogFooter></DialogContent>
                </Dialog>
            </div>
            {loading ? <div className="h-64 rounded-2xl bg-white border border-border animate-pulse" /> : items.length === 0 ? <EmptyState title="Sin posts aún" description="Crea tarjetas para tu Social Wall." /> : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((s) => (
                        <div key={s.id} className="rounded-2xl bg-white border border-border overflow-hidden shadow-soft">
                            <div className="aspect-square bg-secondary">{s.image_url ? <img src={s.image_url} alt={s.title} className="w-full h-full object-cover" /> : null}</div>
                            <div className="p-4">
                                <div className="flex items-center justify-between"><span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary border border-border">{s.platform}</span><span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary border border-border">{s.status}</span></div>
                                <h3 className="mt-2 font-display font-semibold">{s.title}</h3>
                                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{s.description}</p>
                                {s.post_url ? <p className="mt-1 text-[11px] truncate text-[hsl(var(--kche-pink))]">{s.post_url}</p> : <p className="mt-1 text-[11px] text-amber-600">Sin URL del post</p>}
                                <div className="mt-3 flex items-center gap-2">
                                    <Dialog open={editing === s.id} onOpenChange={(o) => !o && setEditing(null)}>
                                        <DialogTrigger asChild><button onClick={() => { setForm({ ...EMPTY, ...s }); setEditing(s.id); }} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-secondary"><Edit3 className="size-3.5" /> Editar</button></DialogTrigger>
                                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Editar</DialogTitle></DialogHeader><Form value={form} onChange={setForm} /><DialogFooter><button onClick={save} className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm">Guardar</button></DialogFooter></DialogContent>
                                    </Dialog>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild><button className="inline-flex items-center gap-1 rounded-lg bg-destructive text-destructive-foreground px-2.5 py-1.5 text-xs"><Trash2 className="size-3.5" /> Borrar</button></AlertDialogTrigger>
                                        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>¿Eliminar?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(s.id)}>Eliminar</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AdminLayout>
    );
}
